import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActionPanel = ({ archiveData }) => {
  const [copiedItem, setCopiedItem] = useState('');
  const [shareMenuOpen, setShareMenuOpen] = useState(false);

  const handleCopy = async (text, itemName) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopiedItem(itemName);
      setTimeout(() => setCopiedItem(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadScreenshot = () => {
    const link = document.createElement('a');
    link.href = archiveData?.screenshot?.url;
    link.download = `polynotary-${archiveData?.id}-screenshot.png`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const handleDownloadMetadata = () => {
    const metadata = {
      id: archiveData?.id,
      originalUrl: archiveData?.originalUrl,
      timestamp: archiveData?.timestamp,
      ipfsHash: archiveData?.ipfsHash,
      transactionHash: archiveData?.transactionHash,
      blockNumber: archiveData?.blockNumber,
      contractAddress: archiveData?.contractAddress,
      verification: {
        blockchain: 'Polygon',
        status: 'Verified',
        confirmations: archiveData?.confirmations
      }
    };

    const blob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `polynotary-${archiveData?.id}-metadata.json`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShare = (platform) => {
    const shareUrl = window.location?.href;
    const shareText = `Verified webpage archive: ${archiveData?.originalUrl} - Notarized on blockchain`;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent('Blockchain Verified Webpage Archive')}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`
    };

    if (shareUrls?.[platform]) {
      window.open(shareUrls?.[platform], '_blank');
    }
    setShareMenuOpen(false);
  };

  const copyableItems = [
    {
      label: 'IPFS URL',
      value: `https://gateway.pinata.cloud/ipfs/${archiveData?.ipfsHash}`,
      icon: 'Database'
    },
    {
      label: 'Transaction Hash',
      value: archiveData?.transactionHash,
      icon: 'Link'
    },
    {
      label: 'Archive URL',
      value: window.location?.href,
      icon: 'Globe'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Download" size={20} className="text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Actions & Downloads</h3>
      </div>
      {/* Download Actions */}
      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            variant="outline"
            iconName="Image"
            iconPosition="left"
            onClick={handleDownloadScreenshot}
            fullWidth
          >
            Download Screenshot
          </Button>
          
          <Button
            variant="outline"
            iconName="FileText"
            iconPosition="left"
            onClick={handleDownloadMetadata}
            fullWidth
          >
            Download Metadata
          </Button>
        </div>
      </div>
      {/* Copy Actions */}
      <div className="space-y-3 mb-6">
        <h4 className="text-sm font-medium text-foreground">Quick Copy</h4>
        {copyableItems?.map((item, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <Icon name={item?.icon} size={14} className="text-muted-foreground flex-shrink-0" />
              <span className="text-sm text-muted-foreground">{item?.label}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName={copiedItem === item?.label ? "Check" : "Copy"}
              onClick={() => handleCopy(item?.value, item?.label)}
              className={copiedItem === item?.label ? "text-success" : ""}
            >
              {copiedItem === item?.label ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        ))}
      </div>
      {/* Share Actions */}
      <div className="relative">
        <h4 className="text-sm font-medium text-foreground mb-3">Share Verification</h4>
        <Button
          variant="outline"
          iconName="Share2"
          iconPosition="left"
          onClick={() => setShareMenuOpen(!shareMenuOpen)}
          fullWidth
        >
          Share Archive
        </Button>

        {shareMenuOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-md shadow-elevated z-50">
            <div className="p-2 space-y-1">
              <button
                onClick={() => handleShare('twitter')}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-smooth"
              >
                <Icon name="Twitter" size={16} />
                <span>Share on Twitter</span>
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-smooth"
              >
                <Icon name="Linkedin" size={16} />
                <span>Share on LinkedIn</span>
              </button>
              <button
                onClick={() => handleShare('email')}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-smooth"
              >
                <Icon name="Mail" size={16} />
                <span>Share via Email</span>
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Verification Badge */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-center p-4 bg-success/10 border border-success/20 rounded-md">
          <div className="flex items-center space-x-2">
            <Icon name="ShieldCheck" size={20} className="text-success" />
            <div className="text-center">
              <p className="text-sm font-medium text-success">Blockchain Verified</p>
              <p className="text-xs text-success/80">Immutable proof of existence</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionPanel;